// Wonder theme — Gestion robuste des variantes (ATC fiable)
// -------------------------------------------------------
// Points clés :
// - Met à jour l'input name="id" dès le chargement et à chaque changement
// - Active le bouton Ajouter au panier si variant disponible OU inventory_policy === 'continue'
// - Pré-sélectionne automatiquement une variante contenant "Noir" (sans ID en dur)
// - Filtre la galerie sur l'image mise en avant de la variante

if (!customElements.get("variant-options")) {
  customElements.define(
    "variant-options",
    class VariantOptions extends HTMLElement {
      whenLoaded = Promise.all([customElements.whenDefined("gallery-section")]);

      constructor() {
        super();
        this.addEventListener("change", this.onVariantChange);
        this.addEventListener("keydown", this.onKeyDown);
        // Section du produit (autour des boutons etc.)
        this.container = document.querySelector(
          `section[data-product-handle="${this.getAttribute(
            "data-product-handle"
          )}"]`
        );
      }

      connectedCallback() {
        this.whenLoaded.then(() => {
          this.initialize();
          // this.preselectNoir();
        });
      }

      /* =========================
         Utils
      ========================= */

      // Autorisé à vendre si available=true OU inventory_policy === 'continue'
      canSellPolicy(v) {
        const p = v?.inventory_policy; // peut être undefined selon le JSON
        return v?.available === true || p === "continue";
      }

      // Cible le bon form/bouton (Wonder)
      getAddToCartForm() {
        return (
          this.container?.querySelector('[data-type="add-to-cart-form"]') ||
          document.querySelector('[data-type="add-to-cart-form"]')
        );
      }

      // Force l'état du bouton (sécurité après chaque rendu)
      enforceAddButton() {
        const form = this.getAddToCartForm();
        const btn = form?.querySelector('[name="add"]');
        const txt = btn?.querySelector("span");
        if (!btn || !txt || !this.currentVariant) return;

        if (this.canSellPolicy(this.currentVariant)) {
          btn.removeAttribute("disabled");
          if (window.variantStrings?.addToCart)
            txt.textContent = window.variantStrings.addToCart;
        } else {
          btn.setAttribute("disabled", "disabled");
          if (window.variantStrings?.soldOut)
            txt.textContent = window.variantStrings.soldOut;
        }
      }

      /* =========================
         Init / preset
      ========================= */

      initialize() {
        this.updateOptions();
        this.updateMasterId();
        this.updateGallery();
        this.updateVariantStatuses();
        this.updateDropdownButtons();

        // ✅ Ajouts pour sécuriser l'état initial
        this.updateVariantInput(); // écrit l’ID de variante dans les forms (ATC + installments)
        this.enforceAddButton(); // active/désactive le bouton selon la vendabilité
      }

      preselectNoir() {
        const data = this.getVariantData?.();
        if (!data || !data.length) return;

        // Cherche la 1ʳᵉ variante dont une option contient "Noir"
        const defaultVariant =
          data.find((v) =>
            v.options?.some((opt) => /noir/i.test(String(opt)))
          ) || null;

        if (!defaultVariant) return;

        // Applique la sélection
        setTimeout(() => {
          this.options = [...defaultVariant.options];
          this.updateMasterId();

          // coche visuellement la bonne option
          const radios = this.querySelectorAll('input[type="radio"]');
          radios.forEach((radio) => {
            if (defaultVariant.options.includes(radio.value)) {
              radio.checked = true;
              radio.dispatchEvent(new Event("change", { bubbles: true }));
            }
          });

          // mises à jour complètes
          this.updateOptions();
          this.updateVariantInput();
          this.updateURL?.();
          this.updateMedia();
          this.renderProductInfo();
          this.enforceAddButton();
          this.updateGallery();
          this.updateVariantStatuses();
          this.updateDropdownButtons();
        }, 100);
      }

      /* =========================
         Events
      ========================= */

      onKeyDown(event) {
        if (event.key === "Enter" || event.keyCode === 13) {
          const input = event.target.querySelector("input");
          input?.click();
        }
      }

      onVariantChange() {
        this.updateOptions();
        this.updateMasterId();
        this.updateGallery();
        this.updateVariantStatuses();
        this.updateDropdownButtons();
        this.updateVariantInput();
        this.updateMedia();
        this.renderProductInfo();

        // sécurité : impose l’état du bouton
        this.enforceAddButton();
      }

      /* =========================
         UI updates
      ========================= */

      updateGallery() {
        const mediaGallery = document.getElementById(
          `MediaGallery-${this.dataset.section}`
        );
        if (!mediaGallery || !this.currentVariant) return;

        const variantMediaId = this.currentVariant.featured_media?.id;

        const filterSlides = () => {
          const slides = mediaGallery.querySelectorAll("[data-slide-media-id]");
          slides.forEach((slide) => {
            const slideMediaId = parseInt(
              slide.getAttribute("data-slide-media-id"),
              10
            );
            slide.style.display = slideMediaId === variantMediaId ? "" : "none";
          });

          if (mediaGallery.swiper) {
            mediaGallery.swiper.update();
            mediaGallery.swiper.slideTo(0);
          }
        };

        const waitForSlides = () => {
          const slides = mediaGallery.querySelectorAll("[data-slide-media-id]");
          if (slides.length === 0) {
            setTimeout(waitForSlides, 50);
          } else {
            filterSlides();
          }
        };

        waitForSlides();
      }

      updateOptions() {
        const fieldsets = Array.from(
          this.querySelectorAll(".wt-product__option")
        );
        this.options = fieldsets.map(
          (fieldset) =>
            Array.from(fieldset.querySelectorAll("input")).find(
              (radio) => radio.checked
            )?.value || ""
        );

        fieldsets.forEach((fieldset, index) => {
          const selectedOption = this.options[index];
          if (!selectedOption) return;
          const title = fieldset.querySelector(
            ".wt-product__option__title .value"
          );
          const dropdown = fieldset.querySelector(
            ".wt-product__option__dropdown span"
          );
          if (title) title.innerHTML = selectedOption;
          if (dropdown) dropdown.innerHTML = selectedOption;
        });
      }

      updateMasterId() {
        this.currentVariant = this.getVariantData()?.find((variant) =>
          variant.options?.every((opt, i) => this.options[i] === opt)
        );
      }

      updateVariantInput() {
        // cible le bon form dans la section
        const forms = this.container
          ? this.container.querySelectorAll(
              '[data-type="add-to-cart-form"], .installment'
            )
          : document.querySelectorAll(
              '[data-type="add-to-cart-form"], .installment'
            );

        forms.forEach((form) => {
          const input = form.querySelector('input[name="id"]');
          if (input && this.currentVariant) {
            input.value = this.currentVariant.id;
            input.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });
      }

updateVariantStatuses() {
  const inputs = this.querySelectorAll('input[type="radio"]');
  const data = this.getVariantData();

  // vendable si available OU inventory_policy === 'continue'
  const availableKey = (v) => (v.options || []).join(" / ");
  const available = (data || [])
    .filter((v) => v?.available || v?.inventory_policy === "continue")
    .map(availableKey);

  // IMPORTANT : on récupère l’index d’option via le DOM, pas via input.name
  const fieldsets = Array.from(this.querySelectorAll(".wt-product__option"));

  inputs.forEach((input) => {
    const fieldset = input.closest(".wt-product__option");
    const optionIndex = fieldsets.indexOf(fieldset);
    if (optionIndex === -1) return;

    const test = [...(this.options || [])];
    test[optionIndex] = input.value;

    const isAvailable = available.includes(test.join(" / "));

    const label = input.closest("label");
    input.classList.toggle("disabled", !isAvailable);
    label?.classList.toggle("disabled", !isAvailable);

    // Optionnel : ne pas mettre input.disabled=true pour éviter de “bloquer” la sortie
    // On laisse le CSS gérer l’aspect grisé.
  });
}


      updateDropdownButtons() {
        const fieldsets = Array.from(
          this.querySelectorAll(".wt-product__option")
        );
        fieldsets.forEach((fieldset) => {
          const dropdown = fieldset.querySelector(
            ".wt-product__option__dropdown"
          );
          const selected = fieldset.querySelector("input:checked");
          dropdown?.classList.toggle(
            "wt-product__option__dropdown--unavailable",
            selected?.classList.contains("disabled")
          );
        });
      }

      updateMedia() {
        if (!this.currentVariant?.featured_media) return;
        this.setAttribute("data-variant-id", this.currentVariant.id);
        this.setAttribute(
          "data-featured-image",
          this.currentVariant.featured_media.preview_image?.src || ""
        );
        this.setAttribute(
          "data-featured-image-id",
          this.currentVariant.featured_media.id
        );
      }

      renderProductInfo() {
        if (!this.currentVariant) return;
        const sectionId = this.dataset.section;
        fetch(
          `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${sectionId}`
        )
          .then((res) => res.text())
          .then((htmlString) => {
            const doc = new DOMParser().parseFromString(
              htmlString,
              "text/html"
            );

            const price = doc.getElementById(`price-${sectionId}`);
            const destPrice = document.getElementById(`price-${sectionId}`);
            if (price && destPrice) destPrice.innerHTML = price.innerHTML;

            const inventory = doc.getElementById(`Inventory-${sectionId}`);
            const destInv = document.getElementById(`Inventory-${sectionId}`);
            if (inventory && destInv) destInv.innerHTML = inventory.innerHTML;

            // La section AJAX peut renvoyer un bouton grisé; on réimpose juste après :
            this.enforceAddButton();
          })
          .catch(() => {
            // évite de bloquer en cas d'erreur réseau
            this.enforceAddButton();
          });
      }

      /* =========================
         Data
      ========================= */

      getVariantData() {
        if (!this.variantData) {
          const el = this.querySelector('script[type="application/json"]');
          this.variantData = JSON.parse(el?.textContent || "[]");
        }
        return this.variantData;
      }
    }
  );
}
